"use client";

import { useState } from "react";
import { LuEye, LuMapPin } from "react-icons/lu";
import { FaRegEyeSlash } from "react-icons/fa6";

import { cn } from "~/utils/fontsConfig";
import { usePermissionsContext } from "~/app/providers";

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "~/components/v3/shadcn/Tooltip";

import { GetEventFromSlug } from "../actions";
import { toast } from "react-toastify";
import { CustomizableLayout } from "~/types/CustomizableLayout";

export const EventLocation = ({
    event,
    isSuccessPage = false,
    customization,
}: {
    event: GetEventFromSlug;
    isSuccessPage?: boolean;
    customization?: CustomizableLayout & {
        infobox: {
            bg?: string;
            content?: string;
            label?: string;
        };
    };
}) => {
    const [showLocation, setShowLocation] = useState(false);
    const permissions = usePermissionsContext();

    // HACK: If the component is on the success page (page viewed after ticket purchase), we don't need to show the location or allow edits regardless of permissions
    //TODO @lemmyMwaura: remove hack
    const canViewLocation = !isSuccessPage
        ? permissions.isAdmin || permissions.isManager || permissions.isHost
        : false;

    const isHidden = event.protectedFields?.find(
        (field) => field.name === "location",
    );

    const pseudoLocation = isHidden?.value;

    const getLocationDisplay = () => {
        if (canViewLocation && showLocation)
            return event.location || "Location";
        if (isHidden) return pseudoLocation || "Protected Location";
        return event.location || "Location";
    };

    const handleCopyLocation = () => {
        if ((canViewLocation && showLocation) || !isHidden) {
            navigator.clipboard.writeText(event.location);
            toast.success("Location copied to clipboard");
        }
    };

    return (
        <div>
            <div
                className={cn(
                    "grid grid-cols-[48px_1fr_96px] items-center gap-4",
                    !canViewLocation && "grid-cols-[48px_1fr]",
                )}
            >
                <div
                    className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-lg bg-v3-N-500",
                        customization?.infobox?.bg,
                    )}
                >
                    <LuMapPin
                        className={cn(
                            "h-5 w-5 text-white",
                            customization?.infobox?.content,
                        )}
                    />
                </div>
                <div className="flex flex-col overflow-hidden">
                    <span
                        className={cn(
                            "truncate text-white",
                            customization?.inputLabelClassName,
                        )}
                        title={
                            isHidden
                                ? "Hidden"
                                : event.locationName || "Location"
                        }
                    >
                        {isHidden
                            ? "Location Hidden"
                            : event.locationName || "Location"}
                    </span>
                    <span
                        className={cn(
                            "cursor-pointer truncate text-v3-N-300",
                            customization?.descriptionClassName,
                        )}
                        title={String(getLocationDisplay())}
                        onClick={handleCopyLocation}
                    >
                        {getLocationDisplay()}
                    </span>
                </div>

                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger>
                            <div className="flex justify-end gap-2">
                                {canViewLocation && isHidden && (
                                    <div
                                        className={cn(
                                            "flex h-12 w-12 cursor-pointer items-center justify-center rounded-lg bg-v3-N-500",
                                            customization?.buttons?.secondary,
                                        )}
                                        onClick={() =>
                                            setShowLocation(!showLocation)
                                        }
                                    >
                                        {showLocation ? (
                                            <LuEye
                                                className={cn(
                                                    "h-5 w-5 text-white",
                                                    customization?.infobox
                                                        ?.content,
                                                )}
                                            />
                                        ) : (
                                            <FaRegEyeSlash
                                                className={cn(
                                                    "h-5 w-5 text-white",
                                                    customization?.infobox
                                                        ?.content,
                                                )}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p
                                className={cn(
                                    "w-[200px] text-sm",
                                    customization?.descriptionClassName,
                                )}
                            >
                                The “Hide” and “View Location” actions are only
                                visible to managers and hosts.
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        </div>
    );
};
